import * as React from 'react';
import {connect} from "react-redux";
import enquiryDispatch from '../../ReduxRequests/Dispatch/enquiryDispatch';
import enquiryState from '../../ReduxRequests/State/enquiryState';
import EnquiryDropZone from './EnquiryDropZone';
import EnquirySeparator from './EnquirySeparator';
import IdentityInput from '../Custom/IdentityInput';
import TextField from '@material-ui/core/TextField';
import Select from 'react-select';
import {COMMON_FORM_STAGE, identityOptions} from '../../Config/appConfig';
import {CoverNote, Option, EnquiryByIdAndChassisNumber, EnquiryByQrCode} from '../../Config/dataModel';
import jsQR from 'jsqr';
import { BrowserQRCodeReader } from '@zxing/library';
import util from '../../Util/util';
import ErrorHandler from '../ErrorHandler/ErrorHandler';
import {throwErrorClient, assertAllTrue, clearErrors} from '../../Util/errorUtil';
import TranslatorInjectParent from '../Translator/TranslatorInjectParent';
import Translator from '../Translator/Translator';
import { reactSelectStyles } from '../constants';

const btnSpinner = require('../../resource/img/button-loading.gif');

interface EnquiryFormProps {
    insurerOptions: Option[],
    enquiryResult?: CoverNote[],
    enquiryByIdAndChassisNumber?: (data: EnquiryByIdAndChassisNumber) => void,
    enquiryByQrCode?: (data: EnquiryByQrCode) => void,
    setIdentity: (identity: Option, identityNo: string, checksum: string) => void,
    setResult: (result: CoverNote[]) => void,
    setInsurer: (insurer: Option) => void
}

interface EnquiryFormState {
    identity: Option,
    identityNo: string,
    checksum: string,
    chassisNo: string,
    insurer: Option,
    dropZoneLoading: boolean,
    btnLoading: boolean
}

class EnquiryForm extends React.Component<EnquiryFormProps, EnquiryFormState> {
    public constructor(props: EnquiryFormProps) {
        super(props);
        this.state = {...{
            dropZoneLoading: false
        }, ...this.getInitialState()};
        this.changeIdentity = this.changeIdentity.bind(this);
        this.changeIdentityNo = this.changeIdentityNo.bind(this);
        this.changeChecksum = this.changeChecksum.bind(this);
        this.changeChassisNo = this.changeChassisNo.bind(this);
        this.changeInsurer = this.changeInsurer.bind(this);
        this.clickReset = this.clickReset.bind(this);
        this.clickSubmit = this.clickSubmit.bind(this);
        this.onError = this.onError.bind(this);
    }

    public getInitialState() {
        return {
            identity: identityOptions[0],
            identityNo: '',
            checksum: '',
            chassisNo: '',
            insurer: null,
            btnLoading: false
        };
    }

    public componentWillReceiveProps(nextProps: EnquiryFormProps) {
        if (nextProps.insurerOptions !== this.props.insurerOptions) {
            this.setState({
                insurer: nextProps.insurerOptions[0]
            });
        }
        if (nextProps.enquiryResult !== this.props.enquiryResult) {
            this.setState({
                dropZoneLoading: false,
                btnLoading: false
            });
            this.props.setIdentity(this.state.identity, this.state.identityNo, this.state.checksum);
            this.props.setInsurer(this.state.insurer);
            this.props.setResult(Array.isArray(nextProps.enquiryResult) ? nextProps.enquiryResult : [nextProps.enquiryResult]);
        }
    }

    public changeIdentity(identity: Option) {
        this.setState({
            identity,
            identityNo: '',
            checksum: ''
        });
    }

    public changeIdentityNo(identityNo: string) {
        this.setState({
            identityNo
        });
    }

    public changeChecksum(checksum: string) {
        this.setState({
            checksum
        });
    }

    public changeChassisNo(event: React.ChangeEvent<HTMLInputElement>) {
        event.preventDefault();
        this.setState({
            chassisNo: event.target.value
        });
    }

    public changeInsurer(insurer: Option) {
        if (insurer) {
            this.setState({
                insurer
            });
        }
    }

    public clickReset() {
        clearErrors();
        this.setState(this.getInitialState());
    }

    public clickSubmit() {
        clearErrors();
        let nonBlank = assertAllTrue([
            {code: 191, hasResult: this.state.identityNo.length > 0},
            {code: 191, hasResult: this.state.chassisNo.length > 0},
            {code: 191, hasResult: !!this.state.insurer}
        ]);
        if (!nonBlank) return;

        this.setState({
            btnLoading: true
        });
        const data = {
            ...{
                docId: util.getIdentityNo(this.state.identity.value, this.state.identityNo, this.state.checksum),
				docType: this.state.identity.value,
                chassisNumber: this.state.chassisNo
            }, ...util.getInsurerId(this.state.insurer.value)
        };
        this.props.enquiryByIdAndChassisNumber(data);
    }

    public onDrop(file: any) {
        this.setState({
            dropZoneLoading: true
        });
        this.clickReset();
        let fileReader = new FileReader;
        fileReader.onload = () => {
            let img = new Image;
            img.onload = () => {
                let canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                let ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);

                const imageData = ctx.getImageData(0, 0, img.width, img.height);
                const code = jsQR(imageData.data, imageData.width, imageData.height);
                try {
                    const payload = JSON.parse(code.data);
                    this.checkInsurer(payload);

                    if (code && code.data) this.props.enquiryByQrCode(payload);
                    else this.readQrCodeAgain(img);
                } catch(error) {
                    this.readQrCodeAgain(img);
                }
            };
            img.src = (fileReader.result as string);
        };
        fileReader.readAsDataURL(file[0]);
    }

    public readQrCodeAgain(img: any) {
        const codeReader = new BrowserQRCodeReader();
        codeReader.decodeFromImage(img).then(result => {
            const payload = JSON.parse(result.getText());
            this.checkInsurer(payload);

            if (payload) this.props.enquiryByQrCode(payload);
            else throwErrorClient(190);
        }).catch( error => {
            throwErrorClient(190);
        });
    }

    public checkInsurer(payload: EnquiryByQrCode) {
        let insurer = this.props.insurerOptions.find((elem)=>elem.value === payload.insId);
        if (insurer === undefined) {
            throwErrorClient(192);
            return;
        }
        this.changeInsurer(this.props.insurerOptions.find((elem)=>elem.value === payload.insId));
    }

    public onError() {
        this.setState({
            dropZoneLoading: false,
            btnLoading: false
        });
    }

    public render() {
        return (
            <div className="enquiryForm">
                <TranslatorInjectParent parentPath="enquiry">
                    <EnquiryDropZone
                        onDrop={(file) => this.onDrop(file)}
                        loading={this.state.dropZoneLoading}
                        disabled={this.state.dropZoneLoading || this.state.btnLoading}/>
                    <EnquirySeparator/>
                    <TranslatorInjectParent parentPath="form">
                        <div>
                            <div className="cmInput">
                                <IdentityInput
                                    stage={COMMON_FORM_STAGE.PROCEED}
                                    identity={this.state.identity}
                                    identityNo={this.state.identityNo}
                                    checksum={this.state.checksum}
                                    changeIdentity={this.changeIdentity}
                                    changeIdentityNo={this.changeIdentityNo}
                                    changeChecksum={this.changeChecksum}
                                />
                            </div>
                            <div className="cmInput cmFormInput">
                                <TextField
                                    label={<Translator path="enquiry.form.labels.chassisNumber">Chassis Number</Translator>}
                                    value={this.state.chassisNo}
                                    onChange={this.changeChassisNo}
                                    InputProps={{disableUnderline: true}}
                                />
                            </div>
                            <div className="cmInput cmFormInput">
                                <label className="cmInputLabel">
                                    <Translator path="labels.insurer">Insurance Company</Translator>
                                </label>
                                <Select
                                    styles={{
                                        ...reactSelectStyles,
                                        placeholder: (style) => ({
                                            ...style,
                                            color: "#86a5b0",
                                            marginLeft: 0,
                                            fontStyle: "normal",
                                        }),
                                        input: (style) => ({
                                            ...style,
                                            marginLeft: 0,
                                        }),
                                    }}
                                    className="react-select__container"
                                    classNamePrefix="react-select"
                                    placeholder={<Translator path="enquiry.form.labels.pleaseSelect" />}
                                    options={this.props.insurerOptions}
                                    value={this.state.insurer}
                                    clearable={false}
                                    onChange={this.changeInsurer}/>
                            </div>
                        </div>
                        <ErrorHandler onError={this.onError}/>
                        <div className="cmBtnsContainer">
                            <div className="cmBtnWrapper">
                                <button className="cmBtn cmGreyBtn cmBoldText" onClick={this.clickReset} disabled={this.state.btnLoading}>
                                    <Translator path="btn.reset">Reset</Translator>
                                </button>
                            </div>
                            <div className="cmBtnWrapper">
                                <button className="cmBtn cmBlueBtn cmBoldText" onClick={this.clickSubmit} disabled={this.state.btnLoading}>
                                    <span className="cmBtnText">
                                        <Translator path="btn.submit">Submit</Translator>
                                    </span>
                                    {this.state.btnLoading && <img className="cmBtnSpinner" src={btnSpinner}/>}
                                </button>
                            </div>
                        </div>
                    </TranslatorInjectParent>
                </TranslatorInjectParent>
            </div>
        );
    }
}

export default connect<any, any, EnquiryFormProps>(enquiryState, enquiryDispatch)(EnquiryForm);



// WEBPACK FOOTER //
// ./src/Components/Enquiry/EnquiryForm.tsx